import { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { QrCode, ArrowLeft, CheckCircle, RefreshCw, AlertCircle } from 'lucide-react';
import { useAgent } from '../../context/AgentContext';
import Button from '../../components/Button';

export default function StepPair() {
  const { goNext, goPrev } = useOutletContext();
  const {
    whatsappPairing,
    checkWhatsAppStatus,
    refreshQRCode,
    isLoading,
    error,
  } = useAgent();

  const [countdown, setCountdown] = useState(120);
  const [localError, setLocalError] = useState(null);

  // Derive status from whatsappPairing state
  const status = whatsappPairing?.status || 'waiting';
  const qrImageUrl = whatsappPairing?.qrImageUrl;

  // Use ref to track if we're connected (to stop intervals)
  const isConnectedRef = useRef(status === 'connected');
  // Use ref for countdown interval so we can clear it from handleRefreshQR
  const countdownIntervalRef = useRef(null);

  // Update connected ref when status changes
  useEffect(() => {
    if (status === 'connected') {
      isConnectedRef.current = true;
      // Clear countdown when connected
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }
  }, [status]);

  // Countdown timer effect - runs once on mount
  useEffect(() => {
    // Don't start if already connected
    if (isConnectedRef.current) return;

    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, []); // Empty dependency - only run on mount

  // Polling effect - runs once on mount
  useEffect(() => {
    // Don't start if already connected
    if (isConnectedRef.current) return;

    const pollId = setInterval(async () => {
      // Skip if already connected
      if (isConnectedRef.current) {
        clearInterval(pollId);
        return;
      }

      try {
        const result = await checkWhatsAppStatus();
        if (result?.status === 'connected') {
          isConnectedRef.current = true;
          clearInterval(pollId);
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000);

    return () => clearInterval(pollId);
  }, []); // Empty dependency - only run on mount, checkWhatsAppStatus is stable

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleRefreshQR = useCallback(async () => {
    setLocalError(null);
    setCountdown(120);

    // Restart countdown timer
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    try {
      await refreshQRCode();
    } catch (err) {
      setLocalError(err.message || 'Failed to refresh QR code');
    }
  }, [refreshQRCode]);

  const displayError = localError || error;

  return (
    <div className="p-8 max-w-2xl mx-auto animate-fade-in">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-accent">
            <QrCode className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Pair with WhatsApp</h1>
            <p className="text-text-secondary">Scan this QR code with your phone</p>
          </div>
        </div>
      </div>

      <div className="card p-8">
        {status === 'connected' ? (
          <div className="text-center animate-slide-up">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-success/10 mb-6">
              <CheckCircle className="w-12 h-12 text-success" />
            </div>
            <h2 className="text-xl font-bold text-text-primary mb-2">Connected!</h2>
            <p className="text-text-secondary mb-2">
              Your WhatsApp is now linked to your agent
            </p>
            {whatsappPairing?.phone && (
              <p className="text-sm text-text-muted mb-6">
                Phone: {whatsappPairing.phone}
              </p>
            )}
            <Button onClick={goNext}>
              Continue to Final Step
            </Button>
          </div>
        ) : (
          <>
            {/* Error Display */}
            {displayError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-700 text-sm font-medium">Error</p>
                  <p className="text-red-600 text-sm">{displayError}</p>
                </div>
              </div>
            )}

            {/* QR Code Display */}
            <div className="flex justify-center mb-6">
              <div className={`
                relative p-4 bg-white rounded-2xl shadow-soft
                ${isLoading ? 'animate-pulse' : ''}
              `}>
                {qrImageUrl ? (
                  <img
                    src={qrImageUrl}
                    alt="WhatsApp QR Code"
                    className="w-48 h-48 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      setLocalError('Failed to load QR code image');
                    }}
                  />
                ) : (
                  <div className="w-48 h-48 flex items-center justify-center bg-gray-100 rounded-lg">
                    {isLoading ? (
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <p className="mt-2 text-sm text-text-muted">Loading...</p>
                      </div>
                    ) : (
                      <p className="text-sm text-text-muted text-center px-4">
                        QR code not available.<br />
                        Click regenerate below.
                      </p>
                    )}
                  </div>
                )}

                {/* Overlay for connecting state */}
                {isLoading && qrImageUrl && (
                  <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-2xl">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      <p className="mt-2 text-sm text-primary font-medium">Checking...</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Status */}
            <div className="text-center mb-6">
              {countdown > 0 ? (
                <>
                  <p className="text-text-secondary mb-2">
                    Open WhatsApp on your phone and scan this code
                  </p>
                  <p className="text-sm text-text-muted">
                    Code expires in <span className="font-mono font-medium text-primary">{formatTime(countdown)}</span>
                  </p>
                </>
              ) : (
                <p className="text-amber-600 font-medium">
                  QR code expired. Please generate a new one.
                </p>
              )}
            </div>

            {/* Refresh QR Button - always visible when QR is displayed or expired */}
            <div className="text-center">
              <Button
                variant="secondary"
                icon={RefreshCw}
                onClick={handleRefreshQR}
                loading={isLoading}
              >
                {countdown === 0 ? 'Generate New Code' : 'Refresh QR Code'}
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Instructions */}
      {status !== 'connected' && (
        <div className="mt-6 p-4 bg-primary/5 rounded-xl border border-primary/10">
          <h4 className="text-sm font-medium text-primary mb-2">To scan the QR code</h4>
          <ol className="text-sm text-text-secondary space-y-1">
            <li>1. Open WhatsApp on your phone</li>
            <li>2. Tap Menu or Settings</li>
            <li>3. Tap Linked Devices</li>
            <li>4. Tap "Link a Device"</li>
            <li>5. Point your phone at this screen to scan</li>
          </ol>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={goPrev}
        >
          Back
        </Button>
        {status !== 'connected' && (
          <Button
            variant="ghost"
            onClick={goNext}
          >
            Skip for now
          </Button>
        )}
      </div>
    </div>
  );
}